import { DOCUMENT_FIELDS, PROCEDURES_FIELDS } from "@/constants";
import { deepClone } from "@/utils/copy";
import { PROOF_OF_ADDRESS_FIELDS } from "../../constants/proofOfAddress";

export function adaptIdentificationSignal({
  id,
  identificationId,
  status,
  companyName,
  startedUtc,
  lastActionUtc,
  finishDeadlineUtc
}) {
  return {
    id,
    identificationId,
    status,
    companyName,
    startedUtc: `${startedUtc?.substr(0, 19)}Z`,
    lastActionUtc: `${lastActionUtc?.substr(0, 19)}Z`,
    finishDeadlineUtc: `${finishDeadlineUtc?.substr(0, 19)}Z`
  };
}

export function adaptRegistries(registries) {
  return registries?.map(reg => ({
    name: reg.name,
    status: reg.status,
    reasons: adaptReasons(reg.reason)
  }));
}

export function adaptFraudChecks(fraudChecks) {
  const fraudChecksCloned = deepClone(fraudChecks);

  return fraudChecksCloned?.map(fraudCheck => ({
    name: fraudCheck.externalFraudCheckProvider,
    fileId: fraudCheck.fileId,
    status: fraudCheck.fraudCheckStatus,
    reasons: adaptReasons(fraudCheck.fraudCheckFailReasons)
  }));
}

export function adaptRules(rules) {
  return rules?.map(rule => ({
    name: rule.name,
    status: rule.status,
    reasons: adaptReasons(rule.reason)
  }));
}

export function adaptReasons(reason) {
  if (!reason) {
    return null;
  }

  return Array.isArray(reason) ? reason : [reason];
}

export function adaptDate(date, suffix) {
  if (date && suffix) return date + suffix;
  return date;
}

export function adaptIdentification({
  id,
  status,
  companyName,
  document,
  registration,
  media,
  rules,
  fraudChecks,
  registries,
  scores,
  proofOfAddress,
  procedures
}) {
  const documentValidations = document?.validations;

  return {
    id,
    status,
    companyName,
    document: {
      [DOCUMENT_FIELDS.DOC_TYPE]: document?.docType,
      [DOCUMENT_FIELDS.DOCUMENT_SUB_TYPE]: document?.documentSubType,
      [DOCUMENT_FIELDS.PERSONAL_CODE]: document?.personalCode,
      [DOCUMENT_FIELDS.FIRST_NAME]: document?.firstName,
      [DOCUMENT_FIELDS.LAST_NAME]: document?.lastName,
      [DOCUMENT_FIELDS.GENDER]: document?.gender,
      [DOCUMENT_FIELDS.DOC_NUMBER]: document?.docNumber,
      [DOCUMENT_FIELDS.DATE_OF_BIRTH]: document?.dateOfBirth?.substr(0, 10),
      [DOCUMENT_FIELDS.PLACE_OF_BIRTH]: document?.placeOfBirth,
      [DOCUMENT_FIELDS.DATE_OF_EXPIRATION]: document?.dateOfExpiration?.substr(
        0,
        10
      ),
      [DOCUMENT_FIELDS.DATE_OF_ISSUE]: document?.dateOfIssue?.substr(0, 10),
      [DOCUMENT_FIELDS.COUNTRY]: document?.country,
      [DOCUMENT_FIELDS.STATE]: document?.state,
      [DOCUMENT_FIELDS.ADDRESS]: document?.address,
      [DOCUMENT_FIELDS.NATIONALITY]: document?.nationality,
      [DOCUMENT_FIELDS.MRZ_LINE_1]: document?.mrzLine1,
      [DOCUMENT_FIELDS.MRZ_LINE_2]: document?.mrzLine2,
      [DOCUMENT_FIELDS.MRZ_LINE_3]: document?.mrzLine3,
      [DOCUMENT_FIELDS.CATEGORY]: document?.category,
      providerType: document?.providerType,
      electronicIdentityProvider: document?.electronicIdentityProvider
    },
    documentValidations:
      documentValidations &&
      Object.entries(documentValidations).reduce((acc, [key, value]) => {
        acc[key] = value?.map(({ isValid, reason }) => ({
          isValid,
          reason
        }));
        return acc;
      }, {}),
    registration: {
      started: adaptDate(registration?.started?.substr(0, 19), "Z"),
      finished: adaptDate(registration?.finished?.substr(0, 19), "Z"),
      applicationId: registration?.applicationId,
      companyName: registration?.companyName,
      dateOfBirth: adaptDate(
        registration?.dateOfBirth?.substr(0, 10),
        "T00:00"
      ),
      email: registration?.email,
      phone: registration?.phone,
      firstName: registration?.firstName,
      lastName: registration?.lastName,
      middleName: registration?.middleName,
      personalCode: registration?.personalCode,
      companyCode: registration?.companyCode,
      countryCode: registration?.countryCode,
      rejectionReason: registration?.rejectionReason,
      ipAddress: registration?.ipAddress,
      ipCountryCode: registration?.ipCountryCode,
      documentExpirationDate: adaptDate(
        registration?.documentExpirationDate?.substr(0, 10),
        "T00:00"
      ),
      taxIdentificationNumber: registration?.taxIdentificationNumber,
      countryOfBirth: registration?.countryOfBirth,
      cityOfBirth: registration?.cityOfBirth,
      address: registration?.address,
      vatNumber: registration?.vatNumber,
      nationality: registration?.nationality
    },
    media: media?.map(file => ({
      created: `${file?.created?.substr(0, 19)}Z`,
      fileType: file?.fileType,
      givenDocumentType: file?.givenDocumentType,
      givenDocumentSideType: file?.givenDocumentSideType,
      fileExtension: file?.fileExtension,
      autoCaptureConfidenceRate: file?.autoCaptureConfidenceRate,
      verificationAttempts: file?.verificationAttempts,
      url: file?.url
    })),
    rules: adaptRules(rules),
    fraudChecks: adaptFraudChecks(fraudChecks),
    registries: adaptRegistries(registries),
    scores: scores?.map(score => ({
      name: score?.name,
      value: score?.value,
      threshold: score?.threshold
    })),
    proofOfAddress: {
      [PROOF_OF_ADDRESS_FIELDS.FIRST_NAME]: proofOfAddress?.firstName,
      [PROOF_OF_ADDRESS_FIELDS.LAST_NAME]: proofOfAddress?.lastName,
      [PROOF_OF_ADDRESS_FIELDS.STREET]: proofOfAddress?.street,
      [PROOF_OF_ADDRESS_FIELDS.COUNTRY]: proofOfAddress?.country,
      [PROOF_OF_ADDRESS_FIELDS.CITY]: proofOfAddress?.city,
      [PROOF_OF_ADDRESS_FIELDS.PARISH]: proofOfAddress?.parish,
      [PROOF_OF_ADDRESS_FIELDS.POSTAL_CODE]: proofOfAddress?.postalCode,
      [PROOF_OF_ADDRESS_FIELDS.STATE]: proofOfAddress?.state,
      [PROOF_OF_ADDRESS_FIELDS.ISSUE_DATE]: proofOfAddress?.issueDate
    },
    procedures: {
      [PROCEDURES_FIELDS.ID]: procedures?.id,
      [PROCEDURES_FIELDS.VERSION_ID]: procedures?.versionId,
      [PROCEDURES_FIELDS.APPLICATION_ID]: procedures?.applicationId,
      [PROCEDURES_FIELDS.SELFIE_TYPES]: procedures?.selfieTypes,
      [PROCEDURES_FIELDS.ACCEPTABLE_DOCUMENTS]: procedures?.acceptableDocuments,
      [PROCEDURES_FIELDS.IMPORTANTS]: procedures?.importants,
      [PROCEDURES_FIELDS.COMMENT]: procedures?.comment
    }
  };
}
